import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { BRAND_LOGO, TRAINING_LINKS } from '../../constants';
import './Sidebar.css';

interface SidebarProps {
  id?: string;
  /** Drawer state below 992px. Ignored by the desktop CSS. */
  isOpen?: boolean;
  /** When given, renders the drawer's close button (hidden at 992px and up). */
  onClose?: () => void;
  closeButtonRef?: React.Ref<HTMLButtonElement>;
}

const Sidebar: React.FC<SidebarProps> = ({ id, isOpen = false, onClose, closeButtonRef }) => {
  const location = useLocation();
  const { user, logout } = useAuth();
  
  const isActive = (path: string) => {
    return location.pathname === path || (location.pathname === '/' && path === '/reports');
  };

  // Class and aria-current from one place, so the visual and announced
  // "current page" never disagree.
  const navLinkProps = (path: string) => {
    const active = isActive(path);
    return {
      className: `nav-link ${active ? 'active' : ''}`,
      'aria-current': active ? ('page' as const) : undefined,
    };
  };

  return (
    <aside id={id} className={`sidebar${isOpen ? ' sidebar--open' : ''}`}>
      <div className="sidebar-brand">
        <div className="brand-content">
          <img src={BRAND_LOGO.src} alt={BRAND_LOGO.alt} className="brand-logo" width="117" height="40" />
        </div>
        {onClose && (
          <button
            type="button"
            ref={closeButtonRef}
            className="sidebar-close"
            aria-label="Close navigation menu"
            onClick={onClose}
          >
            <i className="bi bi-x-lg" aria-hidden="true"></i>
          </button>
        )}
      </div>
      
      <nav className="sidebar-nav">
        <div className="nav-section">
          <div className="nav-section-title">Main</div>
          <div className="nav-item">
            <Link to="/upload" {...navLinkProps('/upload')}>
              <i className="bi bi-cloud-upload nav-icon"></i>
              Upload Report
            </Link>
          </div>
          <div className="nav-item">
            <Link to="/reports" {...navLinkProps('/reports')}>
              <i className="bi bi-file-text nav-icon"></i>
              My Reports
            </Link>
          </div>
        </div>

        <div className="nav-section">
          <div className="nav-section-title">Training</div>
          {TRAINING_LINKS.map((link) => (
            <div className="nav-item" key={link.href}>
              <a
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                className="nav-link"
              >
                <i className={`bi ${link.icon} nav-icon`}></i>
                {link.label}
              </a>
            </div>
          ))}
        </div>

        <div className="nav-section">
          <div className="nav-section-title">Account</div>
          <div className="nav-item">
            <Link to="/account" {...navLinkProps('/account')}>
              <i className="bi bi-person-circle nav-icon"></i>
              Account
            </Link>
          </div>
          <div className="nav-item">
            <button onClick={logout} className="nav-link nav-link-button">
              <i className="bi bi-box-arrow-right nav-icon"></i>
              Sign Out
            </button>
          </div>
        </div>
      </nav>
      
      <div className="sidebar-footer">
        <div className="user-profile">
          <div className="user-avatar">
            {user?.name ? user.name.split(' ').map(n => n[0]).join('').toUpperCase() : 'U'}
          </div>
          <div className="user-details">
            <div className="user-name">{user?.name || 'User'}</div>
          </div>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;